import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useDataChannel,
  useTranscriptions,
} from '@livekit/components-react';
import { getEvalDashboardOptions, getEvalWatchTokenOptions } from '@/api/generated/@tanstack/react-query.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { StatusPill } from '@/components/Evals/format';
import CarouselSlideRenderer from '@/scenes/Room/CarouselSlideRenderer';
import type { InteractiveElement } from '@/scenes/Room/atom';
import { useDebugChannel } from '@/scenes/Room/Debug/useDebugChannel';
import { TimelineTab } from '@/scenes/Room/Debug/TimelineTab';
import { SystemPromptTab } from '@/scenes/Room/Debug/SystemPromptTab';
import { UIObserverTab } from '@/scenes/Room/Debug/UIObserverTab';
import { ConfigTab } from '@/scenes/Room/Debug/ConfigTab';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/runs/$runId/live')({
  component: LiveRunPage,
});

function LiveRunPage() {
  const { runId } = Route.useParams();
  const watch = useQuery({ ...getEvalWatchTokenOptions({ path: { run_id: runId } }), retry: 3, staleTime: Infinity });
  const dashboard = useQuery({ ...getEvalDashboardOptions(), refetchInterval: 5000 });
  const run = dashboard.data?.active_runs.find((r) => r.id === runId);

  return (
    <EvalShell
      title="Watching a live run"
      crumbs={[{ label: 'Dashboard', to: '/evals' }, ...(run ? [{ label: 'Experiment', to: '/evals/experiments/$experimentId', params: { experimentId: run.experiment_id } }] : [])]}
      subtitle={run ? `${run.fixture_key} · ${run.persona} learner` : undefined}
      actions={
        <div className="flex items-center gap-2 text-[12px] text-neutral-500">
          {run ? (
            <>
              <StatusPill status={run.status} />
              <span className="font-mono">
                {run.progress.learner_turns ?? 0}/{run.turns} turns
                {run.progress.script_step != null ? ` · step ${run.progress.script_step + 1}` : ''}
              </span>
            </>
          ) : (
            <span>Run finished or not started</span>
          )}
          {watch.data && (
            <Link to="/reviews/$roomId" params={{ roomId: watch.data.room_id }} className="font-medium text-neutral-700 hover:underline">
              Review timeline
            </Link>
          )}
        </div>
      }
    >
      {!watch.data ? (
        watch.isError ? (
          <p className="text-sm text-neutral-500">The run has no room yet. It starts once the runner picks it up.</p>
        ) : (
          <Skeleton className="h-96 rounded-3xl" />
        )
      ) : (
        <LiveKitRoom serverUrl={watch.data.url} token={watch.data.token} connect audio={false} video={false}>
          <RoomAudioRenderer />
          <LiveRoomBody />
        </LiveKitRoom>
      )}
    </EvalShell>
  );
}

type ScreenEvent = { method: string; element: InteractiveElement };

function LiveRoomBody() {
  useDebugChannel();
  const [screen, setScreen] = useState<InteractiveElement | null>(null);
  const [shown, setShown] = useState<InteractiveElement[]>([]);
  useDataChannel('eval_screen', (msg) => {
    const event = JSON.parse(new TextDecoder().decode(msg.payload)) as ScreenEvent;
    if (event.method === 'show_interactive_element' && event.element?.type) {
      setScreen(event.element);
      setShown((prev) => [event.element, ...prev].slice(0, 30));
    } else if (['close_interactive_element', 'dismiss_interactive_element', 'hide_interactive_element'].includes(event.method)) {
      setScreen(null);
    }
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
      <div className="space-y-4">
        <Panel
          title="Learner's screen"
          aside={<StartAudio label="Listen" className="h-7 rounded-full bg-[#AAE73D] px-3 text-[12px] font-medium text-neutral-900" />}
        >
          <div className="flex min-h-72 items-center justify-center rounded-2xl bg-neutral-950 p-6">
            {screen ? (
              <div className="w-full max-w-2xl">
                <CarouselSlideRenderer
                  readOnly
                  slide={{ id: String(screen.id), type: 'interactive', title: screen.title || '', interactiveElement: screen }}
                />
              </div>
            ) : (
              <p className="text-sm text-neutral-500">Nothing on screen</p>
            )}
          </div>
          {shown.length > 0 && (
            <p className="mt-2 text-[11px] text-neutral-400">
              Shown so far: {shown.map((el) => `${el.type}${el.title ? ` “${el.title}”` : ''}`).join(' · ')}
            </p>
          )}
        </Panel>
        <Panel title="Transcript">
          <LiveTranscript />
        </Panel>
      </div>
      <div className="h-[calc(100vh-12rem)] overflow-hidden rounded-3xl bg-zinc-950 font-mono">
        <Tabs defaultValue="timeline" className="flex h-full flex-col">
          <TabsList className="m-2 bg-zinc-900">
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="prompt">Prompt</TabsTrigger>
            <TabsTrigger value="observer">UI observer</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
          </TabsList>
          <TabsContent value="timeline" className="min-h-0 flex-1 overflow-y-auto">
            <TimelineTab />
          </TabsContent>
          <TabsContent value="prompt" className="min-h-0 flex-1 overflow-y-auto">
            <SystemPromptTab />
          </TabsContent>
          <TabsContent value="observer" className="min-h-0 flex-1 overflow-y-auto">
            <UIObserverTab />
          </TabsContent>
          <TabsContent value="config" className="min-h-0 flex-1 overflow-y-auto">
            <ConfigTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LiveTranscript() {
  const transcriptions = useTranscriptions();
  const lines = transcriptions.slice(-40).reverse();
  if (!lines.length) return <p className="text-sm text-neutral-400">Waiting for the first words…</p>;
  return (
    <div className="max-h-96 space-y-1.5 overflow-y-auto">
      {lines.map((t) => {
        const tutor = t.participantInfo.identity.startsWith('greet-agent');
        return (
          <p key={t.streamInfo.id} className="text-[13px] leading-relaxed">
            <span className={cn('mr-2 text-[11px] font-semibold uppercase', tutor ? 'text-emerald-600' : 'text-sky-600')}>
              {tutor ? 'Tutor' : 'Learner'}
            </span>
            <span className="text-neutral-800">{t.text}</span>
          </p>
        );
      })}
    </div>
  );
}
